import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Box,
  IconButton,
  Typography,
  Tooltip,
  CircularProgress,
  Button,
  Grid,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  useDocumentLogs,
  useKnowledgeBaseDocumentLogs,
} from "../../Query/useCompanies";
import { downloadDocumentBlob } from "../../Helpers/documentHelper";
import { AxiosResponse } from "axios";
import CloseIcon from "@mui/icons-material/Close";
import RefreshIcon from "@mui/icons-material/Refresh";
import color from "../../Constants/colors";
import { useRef, useEffect } from "react";

interface LogViewerModalProps {
  open: boolean;
  onClose: () => void;
  documentId: number | undefined;
  useKb?: boolean;
}

const LogViewerModal: React.FC<LogViewerModalProps> = ({
  open,
  onClose,
  documentId,
  useKb = false,
}) => {
  const { t } = useTranslation("documentUpload");

  const kbLogs = useKb
    ? useKnowledgeBaseDocumentLogs(documentId, open)
    : useDocumentLogs(documentId, open);

  const { data, isLoading, isError, refetch } = kbLogs;

  const handleDownloadLogs = () => {
    if (!data?.logs || !documentId) return;
    const pseudoResponse = { data: data.logs } as unknown as AxiosResponse;
    downloadDocumentBlob(pseudoResponse, `document-log-${documentId}.log`);
  };

  const logs: string[] =
    data?.logs?.split("\n").filter((line) => line.trim() !== "") ?? [];

  const logContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (logContainerRef.current && logs.length > 0) {
      logContainerRef.current.scrollTo({
        top: logContainerRef.current.scrollHeight,
      });
    }
  }, [logs]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "16px 20px",
          color: color.primary_dark,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography sx={{ color: "inherit" }}>
            {t("documentUpload.documentLogs")}
          </Typography>

          <Tooltip title={t("documentUpload.refreshLogs")} arrow>
            <IconButton
              onClick={() => refetch()}
              sx={{ color: color.primary_dark }}
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>

        <IconButton onClick={onClose} sx={{ color: "inherit" }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ padding: "16px 20px" }}>
        {isLoading ? (
          <Grid container justifyContent="center" alignItems="center">
            <CircularProgress />
          </Grid>
        ) : isError ? (
          <DialogContentText>
            {t("documentUpload.failedToLoadLogs")}
          </DialogContentText>
        ) : logs.length > 0 ? (
          <Box
            ref={logContainerRef}
            sx={{
              maxHeight: "60vh",
              overflowY: "auto",
              border: `1px solid ${color.primary_dark}`,
              borderRadius: "8px",
              padding: "12px 14px",
              backgroundColor: "rgba(0,0,0,0.04)",
              fontFamily: "monospace",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {logs.map((log, index) => (
              <Typography
                key={index}
                variant="caption"
                component="pre"
                sx={{
                  margin: 0,
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-all",
                  color: color.primary_dark,
                  fontSize: "0.95rem",
                  lineHeight: 1.4,
                }}
              >
                {log}
              </Typography>
            ))}
          </Box>
        ) : (
          <DialogContentText>
            {t("documentUpload.noLogsAvailable")}
          </DialogContentText>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t("close")}</Button>
        <Button
          onClick={handleDownloadLogs}
          disabled={!data?.logs}
          variant="contained"
          sx={{
            backgroundColor: color.accent,
            color: color.white,
            "&:hover": {
              backgroundColor: color.primary_dark,
              color: color.accent,
            },
          }}
        >
          {t("documentUpload.downloadLogs")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LogViewerModal;
